<template>
  <v-dialog
    value="isDialogOpen"
    max-width="600px"
    scrollable
    persistent
    retain-focus
    @keydown.esc="closeDialog"
  >
    <!-- pass through activator slot -->
    <template v-slot:activator="slotData">
      <slot v-if="$scopedSlots.activator" name="activator" v-bind="slotData" />
    </template>

    <v-form ref="form" v-model="isValid" lazy-validation>
      <v-card :loading="isLoading">
        <v-card-title class="headline">
          {{ formTitle }}
        </v-card-title>
        <div class="overflow-y-auto">
          <v-divider></v-divider>
          <v-alert v-if="!isEditingAllowed" type="warning" tile>
            This user is a developer and cannot be edited here.
          </v-alert>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" md="6" class="pt-0">
                  <v-text-field
                    v-model="user.email"
                    label="Email address"
                    :hint="user.email === '' ? 'Required' : ''"
                    :disabled="isDisabled"
                    persistent-hint
                    :rules="[rules.required, rules.emailAddress]"
                    outlined
                    dense
                    required
                    autofocus
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="6" class="pt-3 pt-sm-0">
                  <v-text-field
                    v-model="user.phoneNumber"
                    label="Phone number"
                    :hint="user.phoneNumber === '' ? 'Required' : ''"
                    :disabled="isDisabled"
                    persistent-hint
                    :rules="[rules.required, rules.phoneNumberE164]"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    v-model="user.firstName"
                    label="First name"
                    :hint="user.firstName === '' ? 'Required' : ''"
                    :disabled="isDisabled"
                    persistent-hint
                    :rules="[rules.required]"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <v-text-field
                    v-model="user.lastName"
                    label="Last name"
                    :hint="user.lastName === '' ? 'Required' : ''"
                    :disabled="isDisabled"
                    persistent-hint
                    :rules="[rules.required]"
                    outlined
                    dense
                    required
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-checkbox
                    v-model="user.isOptedIn"
                    :disabled="isDisabled"
                    label="Opt-In to SMS alerts"
                  >
                  </v-checkbox>
                  <v-alert v-show="user.isOptedIn" type="info">
                    If this user has previously unsubscribed with a STOP command, they will not be able to receive
                    any messages from this system until they send a START command.
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-title class="subtitle-1 font-weight-bold">
            Admin Access
          </v-card-title>

          <v-card-text>
            <v-container class="pt-0">
              <v-row>
                <v-col cols="12" class="pt-0">
                  <v-checkbox
                    v-model="user.isAdmin"
                    :disabled="isEditingUserDeveloper || isDisabled"
                    label="Allow this user to access this admin portal"
                  >
                  </v-checkbox>
                  <v-alert v-show="user.isAdmin" type="info">
                    <strong
                      >This user will have full access to this portal</strong
                    >, including the ability to send SMS alerts, view SMS alert
                    history, and add, edit, and delete any recipient or admin.
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </div>

        <v-card-actions>
          <ConfirmDialog
            title="Delete User"
            message="Are you sure you want to delete this user?"
            type="delete"
            ok-button-text="Delete"
            @confirm="doDeleteUser"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                v-show="!isNewUser && isEditingAllowed"
                color="red darken-1 white--text"
                text
                :disabled="isDisabled"
                v-on="on"
              >
                Delete
              </v-btn>
            </template>
          </ConfirmDialog>
          <v-spacer></v-spacer>

          <v-btn
            color="blue darken-1"
            :disabled="isDisabled"
            text
            @click="closeDialog"
          >
            Cancel
          </v-btn>

          <v-btn
            v-show="isEditingAllowed"
            color="blue darken-1"
            :loading="isDisabled"
            :disabled="!isValid"
            text
            @click="doSaveUser"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import validators from "@/mixins/validators";
import ConfirmDialog from "@/components/ConfirmDialog";
import store from "@/store/store";
import { User } from "@/store/modules/users";

const LOADING_MESSAGE_LOAD_USER = "Loading user data...";
const LOADING_MESSAGE_DELETE_USER = "Deleting user...";
const LOADING_MESSAGE_CREATE_USER = "Creating new user...";
const LOADING_MESSAGE_UPDATE_USER = "Updating user...";

const SUCCESS_MESSAGE_CREATE_USER = "User created.";
const SUCCESS_MESSAGE_UPDATE_USER = "User updated.";
const SUCCESS_MESSAGE_DELETE_USER = "User deleted.";

export default {
  components: {
    ConfirmDialog,
  },
  mixins: [validators],
  props: {
    userId: {
      type: String,
      default: null,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDialogOpen: true,
      isLoading: true,
      loadingMessage: "",
      isValid: false,
      user: new User(),
    };
  },
  computed: {
    isDisabled() {
      return this.isLoading;
    },
    isNewUser() {
      return this.userId === "new";
    },
    isEditingUserDeveloper() {
      if (
        typeof this.user === "undefined" ||
        typeof this.user.roles === "undefined"
      )
        return false;

      return this.user.roles.indexOf("Developer") !== -1;
    },
    userName() {
      return `${this.user.lastName}, ${this.user.firstName}`;
    },
    formTitle() {
      return this.isNewUser ? "New User" : this.userName;
    },
    isEditingAllowed() {
      return !this.isEditingUserDeveloper || this.isDeveloper;
    },
    ...mapGetters("auth", ["isDeveloper"]),
  },
  methods: {
    closeDialog() {
      this.isDialogOpen = false;
      this.$emit("input", false);
      this.$router.push({ name: "Users" });
    },
    doSaveUser() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isLoading = true;
      let saveUserPromise = null;
      let successMsg = null;

      if (this.isNewUser) {
        this.loadingMessage = LOADING_MESSAGE_CREATE_USER;
        successMsg = SUCCESS_MESSAGE_CREATE_USER;
        saveUserPromise = this.createUser(this.user);
      } else {
        this.loadingMessage = LOADING_MESSAGE_UPDATE_USER;
        successMsg = SUCCESS_MESSAGE_UPDATE_USER;
        saveUserPromise = this.updateUser(this.user);
      }

      saveUserPromise
        .then(() => {
          this.showSnackbar({ message: successMsg });
          this.closeDialog();
        })
        .catch((err) => {
          this.showSnackbar({ message: err.message, color: "error" });
        })
        .finally(() => {
          this.isLoading = false
        });
    },
    doDeleteUser() {
      this.isLoading = true;
      this.loadingMessage = LOADING_MESSAGE_DELETE_USER;

      this.deleteUser(this.userId)
        .then(() => {
          this.showSnackbar({ message: SUCCESS_MESSAGE_DELETE_USER });
          this.closeDialog();
        })
        .catch((err) => {
          this.showSnackbar({ message: err.message, color: "error" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    ...mapActions("users", ["createUser", "updateUser", "deleteUser"]),
    ...mapActions("snackbar", ["showSnackbar"]),
  },
  beforeRouteEnter(to, from, next) {
    const loadDataPromise = new Promise((resolve, reject) => {
      const userId = to.params.userId;
      store
        .dispatch("users/getEditableUser", userId === "new" ? null : userId)
        .then((user) => {
          resolve(user);
        })
        .catch((err) => {
          reject(err);
        });
    });

    next((vm) => {
      vm.isLoading = true;
      vm.loadingMessage = LOADING_MESSAGE_LOAD_USER;

      loadDataPromise
        .then((result) => {
          vm.user = result;
          vm.isLoading = false;
        })
        .catch((err) => {
          store.dispatch("snackbar/showSnackbar", {
            message: err.message,
            color: "error",
          });
        });
    });
  },
};
</script>

<style scoped></style>
